import { useContext } from 'react';

import { useSearchParams } from 'next/navigation';

import { RefererContext } from '@/modules/activityTracker/RefererProvider';
import { useCurrentLocation } from '@/modules/location/useCurrentLocation';
import { shouldRenderSimilarSash } from '@/modules/product/helpers/shouldRenderSimilarSash';
import { useBespokeCondensedPDP } from '@/modules/product/hooks/useBespokeCondensedPDP';
import { useProductBySlugBasicPDP } from '@/modules/product/hooks/useProductBySlugBasicPDP';
import { useProductBySlugExtendedPDP } from '@/modules/product/hooks/useProductBySlugExtendedPDP';
import { getSimilarSashAndYMALProducts } from '@/modules/similar/helpers';
import { buildFallbackSearchQuery } from '@/modules/similar/helpers/buildFallbackSearchQuery';
import { useSimilarItems } from '@/modules/similar/useSimilarItems';

export function useYouMightAlsoLike(limit: number) {
  const { location } = useCurrentLocation();
  const { data: productBasic, isLoading: isProductBasicLoading } =
    useProductBySlugBasicPDP();
  const { data: productExtended, isLoading: isProductExtendedLoading } =
    useProductBySlugExtendedPDP();
  const searchParams = useSearchParams();
  const referer = useContext(RefererContext) || null;
  const isSimilarSashVisible = shouldRenderSimilarSash({
    product: productBasic,
    params: Object.fromEntries(searchParams.entries()),
    referer,
  });
  const enabled = Boolean(
    productBasic?.id && productExtended?.attributes.product_type_name
  );

  const { data: similarProductsRes, isLoading: isSimilarItemsLoading } =
    useSimilarItems({
      productId: productBasic?.id,
      params: {
        limit,
        search_fallback_location: location,
        search_fallback_what: buildFallbackSearchQuery({
          productExtended,
        }),
      },
      enabled,
    });

  const products = similarProductsRes?.data?.objects || [];
  const colourId = productExtended?.attributes?.colour?.[0]?.id;

  const { similarSashCount: sashCount } = useBespokeCondensedPDP();

  const { youMightAlsoLike: YMALProducts } = getSimilarSashAndYMALProducts(
    products,
    isSimilarSashVisible,
    colourId,
    sashCount
  );

  return {
    products: YMALProducts,
    isLoading:
      isProductBasicLoading ||
      isProductExtendedLoading ||
      isSimilarItemsLoading,
  };
}
